import React, { useState, useContext } from 'react';
import { Container, Box, TextField, Button, Snackbar, Alert, InputAdornment } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AnimatePresence, motion } from 'framer-motion';
import '../styles/css/Contact.css';
import { LanguageContext } from '../App';

const WHATSAPP_NUMBER = '972524636767';

const Contact = () => {
  const [quickForm, setQuickForm] = useState({ name: '', date: '', phone: '' });
  const [quickFormErrors, setQuickFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const { language, translations } = useContext(LanguageContext);
  const t = translations[language];

  const handleQuickFormChange = (e) => {
    const { name, value } = e.target;
    setQuickForm((prev) => ({ ...prev, [name]: value }));
    if (quickFormErrors[name]) {
      setQuickFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateQuickForm = () => {
    const errors = {};
    if (!quickForm.name.trim()) {
      errors.name = t.quickFormNameError;
    }
    if (quickForm.phone.trim() && !/^[\d\-+\s()]{7,}$/.test(quickForm.phone.trim())) {
      errors.phone = t.quickFormPhoneError;
    }
    return errors;
  };

  const handleQuickFormSubmit = (e) => {
    e.preventDefault();
    const errors = validateQuickForm();
    if (Object.keys(errors).length > 0) {
      setQuickFormErrors(errors);
      return;
    }

    setIsSubmitting(true);

    const message = t.quickFormWhatsappTemplate(
      quickForm.name.trim(),
      quickForm.date,
      quickForm.phone.trim()
    );
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;

    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');

    setSnackbarOpen(true);
    setIsSubmitting(false);
    setJustSubmitted(true);
    setQuickForm({ name: '', date: '', phone: '' });

    setTimeout(() => setJustSubmitted(false), 2200);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  return (
    <Box className="contact-section">
      <Container className="contact-container">
        <h2 className="section-title">
          {t.contactTitle}
        </h2>
        <p className="section-subtitle">
          {t.contactSubtitle}
        </p>
        <p className="section-subtitle">
          {t.contactSubtitle2}
        </p>

        <Box className="contact-grid-wrapper">
          <Box className="contact-info-container">
            <Box className="contact-info">
              <h3 className="contact-info-title">
                {t.contactInfoTitle}
              </h3>

              <Box className="contact-method">
                <PhoneIcon className="contact-icon" />
                <Box>
                  <p className="contact-method-title">
                    {t.contactPhoneTitle}
                  </p>
                  <p className="contact-method-value">
                    <a href="tel:+972524636767" className="contact-link">
                      052-4636767
                    </a>
                  </p>
                </Box>
              </Box>

              <Box className="contact-method">
                <WhatsAppIcon className="contact-icon" />
                <Box>
                  <p className="contact-method-title">
                    {t.contactWhatsappTitle}
                  </p>
                  <p className="contact-method-value" dir="ltr">
                    <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" className="contact-link">
                      +972-52-4636767
                    </a>
                  </p>
                </Box>
              </Box>

              <Box className="contact-method">
                <EmailIcon className="contact-icon" />
                <Box>
                  <p className="contact-method-title">
                    {t.contactEmailTitle}
                  </p>
                  <p className="contact-method-value">
                    <a href="mailto:info@studiorami.click" className="contact-link">
                      info@studiorami.click
                    </a>
                  </p>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            component="form"
            className="quick-contact-form"
            onSubmit={handleQuickFormSubmit}
            noValidate
            dir={language === 'he' ? 'rtl' : 'ltr'}
          >
            <TextField
              fullWidth
              name="name"
              label={t.quickFormName}
              placeholder={t.quickFormNamePlaceholder}
              value={quickForm.name}
              onChange={handleQuickFormChange}
              error={Boolean(quickFormErrors.name)}
              helperText={quickFormErrors.name}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              type="date"
              name="date"
              label={t.quickFormDate}
              value={quickForm.date}
              onChange={handleQuickFormChange}
              margin="normal"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EventIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              name="phone"
              type="tel"
              label={t.quickFormPhone}
              placeholder={t.quickFormPhonePlaceholder}
              value={quickForm.phone}
              onChange={handleQuickFormChange}
              error={Boolean(quickFormErrors.phone)}
              helperText={quickFormErrors.phone}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isSubmitting}              
              className={`quick-form-submit ${justSubmitted ? 'quick-form-submit--success' : ''}`}
              component={motion.button}
              whileHover={{ scale: justSubmitted ? 1 : 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {justSubmitted ? t.quickFormSuccessTitle : (isSubmitting ? t.quickFormSubmitting : t.quickFormSubmit)}
            </Button>
          </Box>
        </Box>
      </Container>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" variant="filled" className="quick-form-toast">
          <div className="quick-form-toast-title">
            {t.quickFormSuccessTitle}
          </div>
          <div className="quick-form-toast-body">
            {t.quickFormSuccessBody}
          </div>
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Contact;
